import { Inject, Injectable, InjectionToken } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { Menu, MenuGroupItem, MenuItem, MenuLinkBadge, MenuLinkBadgeNew, MenuPanelItem, StickyState } from '../../domain/menu.domain';
import { UrlUtilityService } from '../../utils/url-utility.service';
import { SecurityService } from '../security.service';
import { LinkRegistryService } from '../link-registry.service';
import { WINDOW_TOKEN } from '../bootstrap.service';

export const MENU_CONFIGURATION = new InjectionToken<Array<unknown>>('menuConfiguration');

// Define a type alias for MenuPanelItem & MenuGroupItem & MenuItem
type MenuItemType = MenuPanelItem & MenuGroupItem & MenuItem;

/**
 * The Menu Service
 */
@Injectable({
  providedIn: 'root'
})
export class MenuService {

  private static readonly UTILITY_TOOLBAR_LIMIT: number = 5;
  private static readonly DEFAULT_MENU_WIDTH: number = 1004;

  /**
   * Boolean for indicating whether a menu is enabled
   */
  enabled: boolean;

  /**
   * Boolean for indicating whether a menu is hidden
   */
  hidden: boolean;

  footerHidden: boolean;

  /**
   * Sticky state change subject
   */
  readonly onStickyStateChangeSubject: BehaviorSubject<StickyState>;
  readonly onMenuWidthChangeSubject: BehaviorSubject<number>;

  readonly onFabMenuHiddenChange: BehaviorSubject<boolean>;

  private menuWidth: number;

  /**
   * Root ID of menu item
   */
  private rootIdToMenuItemUrlForNav: unknown = {};

  private routeHideFabMenu: boolean = false;
  private mobileMenuHideFabMenu: boolean = false;

  /**
   * The constructor of the component.
   *
   *
   * @param menuConfig Menu configuration object
   * @param browserWindow The window object
   * @param securityService Used to check permissions
   * @param linkRegistryService Service for managing links
   */
  constructor(
    @Inject(MENU_CONFIGURATION) private readonly menuConfig: Menu,
    @Inject(WINDOW_TOKEN) private readonly browserWindow: Window,
    private readonly securityService: SecurityService,
    private readonly linkRegistryService: LinkRegistryService
  ) {
    this.enabled = true;

    this.generateRootIdToMenuItemUrlForNav(menuConfig.localNavigation as Array<MenuItemType>);
    this.onStickyStateChangeSubject = new BehaviorSubject<StickyState>(StickyState.FULL);
    // Keep internal width in sync with the subject's initial emission to avoid
    // a duplicate emission when setting the same width first time.
    this.menuWidth = MenuService.DEFAULT_MENU_WIDTH;
    this.onMenuWidthChangeSubject = new BehaviorSubject<number>(MenuService.DEFAULT_MENU_WIDTH);
    this.onFabMenuHiddenChange = new BehaviorSubject<boolean>(false);
  }

  onStickyStateChange(currentState: StickyState): void {
    this.onStickyStateChangeSubject.next(currentState);
  }

  onMenuWidthChange(width: number): void {
    if (this.menuWidth === undefined || this.menuWidth !== width) {
      this.menuWidth = width;
      this.onMenuWidthChangeSubject.next(this.menuWidth);
    }
  }

  /**
   * This function retrieves the menu object
   *
   * @returns Returns the menu object
   */
  getMenu(): Menu {
    return {
      applicationTitle: this.fixUndefined(this.menuConfig.applicationTitle),
      topNavigation: this.fixUndefined(this.menuConfig.topNavigation),
      globalNavigation: this.fixUndefined(this.menuConfig.globalNavigation),
      localNavigation: this.fixUndefined(this.menuConfig.localNavigation),
      utilityToolbar: this.fixUndefined(this.menuConfig.utilityToolbar),
      mobileNavigation: this.fixUndefined(this.menuConfig.mobileNavigation),
      mobileMenuHeaderToolbar: this.getMobileMenuHeaderToolbar()
    };
  }

  /**
   * This function returns whether a menu is enabled or not
   *
   * @returns Returns true if a menu is enabled. Otherwise, false.
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * This function disables menu
   */
  disable(): void {
    this.enabled = false;
  }

  /**
   * This function enables menu
   */
  enable(): void {
    this.enabled = true;
  }

  /**
   * check if we should hide all menus and footer - for instance, on embedded view
   */
  isHidden(): boolean {
    return this.hidden;
  }

  isFooterHidden(): boolean {
    return this.footerHidden || this.hidden;
  }

  /**
   * set menus and footer to hidden
   */
  hide(): void {
    this.hidden = true;
  }

  hideFooter(): void {
    this.footerHidden = true;
  }

  showFooter(): void {
    this.footerHidden = false;
  }

  hideFabMenuForRoute(): void {
    this.routeHideFabMenu = true;
    this.updateFabMenuHiddenState();
  }

  showFabMenuForRoute(): void {
    this.routeHideFabMenu = false;
    this.updateFabMenuHiddenState();
  }

  hideFabMenuForMobileMenu(): void {
    this.mobileMenuHideFabMenu = true;
    this.updateFabMenuHiddenState();
  }

  showFabMenuForMobileMenu(): void {
    this.mobileMenuHideFabMenu = false;
    this.updateFabMenuHiddenState();
  }

  /**
   * This function returns the local navigation root menu id
   *
   * @param stateUrl State url to get root id
   * @returns Returns a local navigation root menu id
   */
  getLocalNavRootMenuId(stateUrl: string): string {
    return this.rootIdToMenuItemUrlForNav[UrlUtilityService.stripToParentStateUrl(stateUrl)];
  }

  /**
   * This function returns the current local navigation root menu id
   *
   * @returns Returns the current local navigation root menu id
   */
  getCurrentLocalNavRootMenuId(): string {
    const currentUrl = this.browserWindow.location.href;
    return this.getLocalNavRootMenuId(currentUrl.slice(currentUrl.indexOf('#') + 1));
  }

  /**
   * This function returns the number of links shown on the utility toolbar
   *
   * @returns Returns the length of the utility toolbar
   */
  getUtilityToolbarLength(): number {
    return MenuService.UTILITY_TOOLBAR_LIMIT;
  }

  getUtilityToolbar(isDesktop: boolean = true): Array<MenuPanelItem> {
    const preProcessedMenus: Array<MenuPanelItem> = this.preProcessUtilityToolBar(this.getMenu().utilityToolbar);
    let menus: Array<MenuPanelItem> = this.limitTo(preProcessedMenus, MenuService.UTILITY_TOOLBAR_LIMIT);
    if (!isDesktop && this.securityService.hasPermission('SEARCH')) {
      menus = menus.length === MenuService.UTILITY_TOOLBAR_LIMIT ? menus.slice(1) : menus;
    }
    return menus;
  }

  /**
   * This function slices an array into a size limit
   *
   * @param array array to slice
   * @param limit Limit of array size
   * @returns Returns slices an array.
   */
  limitTo(array: Array<MenuPanelItem>, limit: number): Array<MenuPanelItem> {
    return array.length > limit ? array.slice(0, limit) : array;
  }

  getLogoUrl(): string {
    let url = this.linkRegistryService.getLink('link_home_page');
    if (url === '#') {
      url = this.linkRegistryService.getLink('link_balances');
    }
    return url;
  }

  getLogoLabelTranslationId(): string {
    const url = this.linkRegistryService.getLink('link_home_page');
    if (url === '#') {
      return 'tdWb.menu.BALANCES_PAGE';
    }
    return 'tdWb.menu.HOME_PAGE';
  }

  showNewBadge(value: string): boolean {
    const menuLinkBadge = MenuLinkBadgeNew.find((x: MenuLinkBadge) => x.link === value);
    return menuLinkBadge !== undefined &&
           (((menuLinkBadge.permission ?? '') === '') || this.securityService.hasPermission(menuLinkBadge.permission));
  }

  updateFabMenuHiddenState(): void {
    // if either is true, hide a fab menu, otherwise show a fab menu
    this.onFabMenuHiddenChange.next(this.routeHideFabMenu || this.mobileMenuHideFabMenu);
  }

  private getMobileMenuHeaderToolbar(): Array<MenuPanelItem> {
    const hasMessagesPermission = this.securityService.hasPermission('MESSAGES');
    const hasSecureMessagePermission = this.securityService.hasPermission('SECURE_MESSAGE');
    let menu = this.fixUndefined(this.menuConfig.mobileMenuHeaderToolbar);
    if (hasMessagesPermission && hasSecureMessagePermission) {
      menu = menu.filter((menuItem: MenuPanelItem) => !menuItem.href?.includes('preferences/messages'));
    }
    return menu;
  }

  /**
   * This function checks if it is an array
   *
   * @param arr Argument to check if it is an array
   * @returns Returns true if argument is an array. Otherwise, false.
   */
  private isArray(arr: unknown): boolean {
    return Array.isArray(arr) || arr instanceof Array;
  }

  /**
   * This function generates root id to menu item
   *
   * @param menuData Menu data
   * @param rootId Root id
   */
  private generateRootIdToMenuItemUrlForNav(
    menuData: Array<MenuItemType>,
    rootId?: string
  ): void {
    menuData.forEach((menuItem: MenuItemType): void => {
      if (menuItem.href) {
        this.rootIdToMenuItemUrlForNav[
          UrlUtilityService.stripToParentStateUrl(UrlUtilityService.stripToStateUrl(menuItem.href))
        ] = rootId === undefined ? menuItem.id : rootId;
      }
      if (menuItem.menu !== undefined) {
        this.generateRootIdToMenuItemUrlForNav(
          menuItem.menu as Array<MenuItemType>,
          rootId === undefined ? menuItem.id : rootId
        );
      }
      if (menuItem.item !== undefined) {
        this.generateRootIdToMenuItemUrlForNav(
          menuItem.item as Array<MenuItemType>,
          rootId === undefined ? menuItem.id : rootId
        );
      }
    });
  }

  private fixUndefined(items: Array<MenuPanelItem>): Array<MenuPanelItem> {
    return this.isArray(items) ? items : [];
  }

  private preProcessUtilityToolBar(menuItems: Array<MenuPanelItem>): Array<MenuPanelItem> {
    return menuItems.filter((item: MenuPanelItem): boolean => {
      if (this.securityService.hasPermission('TRANSFER_OVERVIEW')) {
        return !item.permissions?.includes('UI_TRANSFER_OVERVIEW');
      } else {
        return true;
      }
    });
  }
}
